import { CAUSES, causesOf, titleOf } from "../Issues/kinds";
import { formatDay } from "../Issues/dates";
import { MEASURES } from "../Issues/measure";
import { startsOf } from "./figures";

/** `PeriodCompareChart`'s marker type for an issue kind; Orphaned is not one of its types. */
const TYPES = { errored: "index", indexability: "index", content: "meta", orphaned: "Orphaned" };

/** The issues that started on `start`. */
export const startedOn = (issues, start) => issues.filter((issue) => issue.since === start && issue.sinceKind !== "neverHealthy");

/** Of the issues that started on one day, the one with the most critical cause. */
export const leadOf = (started) => started
    .flatMap((issue) => causesOf(issue).map((cause) => ({ issue, cause })))
    .sort((a, b) => CAUSES.indexOf(a.cause) - CAUSES.indexOf(b.cause))[0].issue;

/** One marker per issue start, worded as the Issues row words its issues, typed by its most critical issue. */
export const issueMarkersOf = (issues) => startsOf(issues).map((start) => {
    const started = startedOn(issues, start);
    const lead = leadOf(started);
    return {
        id: `issue-${start}`,
        date: start,
        type: TYPES[lead.kind],
        title: `${lead.detail || titleOf(lead.kind)}${started.length > 1 ? ` and ${started.length - 1} more` : ""}`,
        description: `Found by the crawl of ${formatDay(lead.foundBy.day)}`,
    };
});

const perDay = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 });

/** Pettitt's change point on `measure` for the issue, as its Issues row reads it; none where the test found none. */
export const changePointOf = (issue, measure) => (issue && MEASURES[measure]?.readingOf(issue)?.pivot) || null;

/** The change point's day and how far it is from the issue start: "17 Jan 2026, 1 day before the start". */
export const changePointDayOf = (changePoint, since) => {
    const days = Math.round((Date.parse(changePoint.day) - Date.parse(since)) / 86400000);
    const distance = days === 0 ? "on the start" : `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ${days < 0 ? "before" : "after"} the start`;
    return `${formatDay(changePoint.day)}, ${distance}`;
};

/**
 * Pettitt's change point on `measure` at the issue start, a debugging aid as on the Issues row's sparkline: its own
 * marker, never merged into an issue start's, however close. None on a measure Issues reads no loss on.
 */
export const changePointMarkersOf = (issue, measure, since) => {
    const changePoint = changePointOf(issue, measure);
    return changePoint ? [{
        id: `pettitt-${measure}`,
        date: changePoint.day,
        type: "Change point",
        title: "Pettitt change point",
        description: `${changePointDayOf(changePoint, since)}: ${perDay.format(changePoint.before)} → ${perDay.format(changePoint.after)} a day`,
    }] : [];
};

/** The issue start a marker from `issueMarkersOf` stands for; none for any other marker. */
export const startOfMarker = (markerId) => markerId?.startsWith("issue-") ? markerId.slice("issue-".length) : null;
